// src/pages/Wallet/components/TokenList.js
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { formatUnits } from 'ethers';
import TokenItem from './TokenItem';
import { getChainLogo } from '../chainLogos';
import { getLogoUrl, DEFAULT_TOKEN_LOGO, prefetchLogos } from '../../../utils/logoCacheUtils';

const TokenList = ({ 
  balance, 
  nativePrice, 
  tokens, 
  tokenPrices, 
  tokenLogos, 
  tokenPriceChanges,
  nativePriceChange, // Add native price change prop
  onTokenClick, 
  onManageTokens 
}) => {
  const { t } = useTranslation();
  const { currentChain } = useContext(NetworkContext);
  
  // Prefetch logos on component mount or when tokens/chain changes
  useEffect(() => {
    if (tokens && tokens.length && currentChain && tokenLogos) {
      prefetchLogos(tokens, currentChain.key, tokenLogos);
    }
  }, [tokens, currentChain, tokenLogos]);
  
  // Get native token symbol
  const nativeTokenSymbol = currentChain && currentChain.nativeTokenName
    ? currentChain.nativeTokenName.includes('(')
      ? currentChain.nativeTokenName.split('(')[1].replace(')', '').trim()
      : currentChain.nativeTokenName
    : 'ETH';
  
  // Format native token balance
  const nativeBalanceDisplay = balance
    ? parseFloat(balance).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 5 })
    : "0.0";
  
  // Calculate native token USD value
  const numericBalance = balance ? parseFloat(balance) : 0;
  const nativeUsdValue = nativePrice && numericBalance
    ? (numericBalance * nativePrice).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 2 })
    : "--";
  
  // Sort tokens by value
  const sortedErc20Tokens = (() => {
    const erc20List = tokens.filter(t => t.token && t.token.type === "ERC-20");
    return erc20List.slice().sort((a, b) => {
      const A = a.token, B = b.token;
      let rawA = a.value, rawB = b.value;
      if (A.decimals) {
        try { rawA = formatUnits(a.value, Number(A.decimals)); } catch {}
      }
      if (B.decimals) {
        try { rawB = formatUnits(b.value, Number(B.decimals)); } catch {}
      }
      const balA = parseFloat(rawA);
      const balB = parseFloat(rawB);
      const usdA = tokenPrices[A.address?.toLowerCase()] || 0;
      const usdB = tokenPrices[B.address?.toLowerCase()] || 0;
      const valA = balA * usdA;
      const valB = balB * usdB;
      if (valA > 0 && valB > 0) {
        return valB - valA;
      } else if (valA > 0 && valB === 0) {
        return -1;
      } else if (valA === 0 && valB > 0) {
        return 1;
      } else {
        // Fix: Handle undefined names safely
        const nameA = A.name || '';
        const nameB = B.name || '';
        return nameA.localeCompare(nameB);
      }
    });
  })();

  // Get chain logo for native token
  const chainLogo = getChainLogo(currentChain?.key);
  
  // Determine color for native token price change
  const nativePriceChangeColor = nativePriceChange > 0 
    ? 'var(--success)' 
    : nativePriceChange < 0 
      ? 'var(--error)' 
      : 'var(--text-tertiary)';
  
  // Format native price change
  const nativePriceChangeStr = nativePriceChange !== undefined && nativePriceChange !== null
    ? `${nativePriceChange > 0 ? '+' : ''}${nativePriceChange.toFixed(2)}%`
    : '';
  
  return (
    <div className="token-list">
      {/* Native Token */}
      <div 
        className="token-container" 
        onClick={() => onTokenClick({ type: "native", symbol: nativeTokenSymbol })}
      >
        <div className="token-row three-column">
          {/* Column 1: Logo */}
          <img 
            src={chainLogo} 
            alt={nativeTokenSymbol}
            className="token-logo"
            onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_TOKEN_LOGO; }}
          />
          
          {/* Column 2: Token name and balance */}
          <div className="token-name-balance">
            <span className="token-name">{currentChain?.nativeTokenName || t('wallet.nativeToken')}</span>
            <div className="token-balance">
              {nativeBalanceDisplay}
            </div>
          </div>
          
          {/* Column 3: Price */}
          <div className="token-price-info">
            <span className="token-usd">${nativeUsdValue}</span>
            {nativePriceChangeStr && (
              <span className="token-price-change" style={{ color: nativePriceChangeColor }}>
                {nativePriceChangeStr}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ERC-20 Tokens */}
      {sortedErc20Tokens.map((tokenObj, idx) => {
        // Get cached or fetch logo URL
        const tokenAddress = tokenObj.token.address?.toLowerCase();
        const logoUrl = tokenAddress && currentChain 
          ? getLogoUrl(tokenAddress, currentChain.key, tokenLogos[tokenAddress])
          : null;
          
        return (
          <TokenItem 
            key={idx} 
            tokenObj={tokenObj} 
            tokenPrice={tokenPrices[tokenAddress] || 0} 
            tokenLogo={logoUrl} 
            priceChange={tokenPriceChanges[tokenAddress]}
            onClick={() => onTokenClick(tokenObj.token)} 
          />
        );
      })}
      
      {/* Manage Tokens Button */}
      <div className="manage-tokens-button" onClick={onManageTokens}>
        <div className="manage-tokens-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
          </svg>
        </div>
        <span>{t('wallet.manageTokens', 'Manage Tokens')}</span>
      </div>
    </div>
  );
};

export default TokenList;